// Footer.jsx
import React, { useState, useEffect }  from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Clock, Shield, Award } from 'lucide-react';
import { TikTokIcon } from './TikTokIcon';
import { trackSocialClick } from '../utils/analytics';
import './Footer.css';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Add these new state variables
  const [secretClicks, setSecretClicks] = useState(0);
  const [secretTimer, setSecretTimer] = useState(null);

  // Add this function to handle the secret pattern
const handleSecretClick = (e) => {
  // Only detect clicks on the footer area
  if (e.target.classList.contains('footer-secret-area')) {
    // Clear any existing timer
    if (secretTimer) {
      clearTimeout(secretTimer);
    }
    
    // Increment click counter
    setSecretClicks(prevClicks => prevClicks + 1);
    
    // Set a timeout to reset the counter if user doesn't complete pattern quickly enough
    const timer = setTimeout(() => {
      setSecretClicks(0);
    }, 2000); // Reset after 2 seconds of inactivity
    
    setSecretTimer(timer);
    
    // If the pattern is completed (5 clicks), navigate to login
    if (secretClicks >= 4) { // Will become 5 after increment
      setSecretClicks(0);
      clearTimeout(timer);
      // Navigate to login page
      window.location.href = '/login';
    }
  }
};

// Clean up the timer when component unmounts
useEffect(() => {
  return () => {
    if (secretTimer) {
      clearTimeout(secretTimer);
    }
  };
}, [secretTimer]);
  
  return (
    <footer className="site-footer"><a className="studio-whatsapp" href="https://wa.me/27650717182?text=Hi%20Two%20Gs%2C%20I%20would%20like%20help%20with%20a%20personalised%20gift." target="_blank" rel="noopener noreferrer" aria-label="Get help with a personalised gift on WhatsApp"><Phone size={16}/><span>A little help?</span></a>
      <div className="footer-invitation"><span className="eyebrow">FOR SOMEONE YOU KNOW BY HEART</span><h2>Let’s make something personal.</h2><Link className="studio-button light" to="/?section=shop">Find their something <span aria-hidden="true">↗</span></Link></div><div className="footer-main">
        <div className="footer-container">
          {/* About Section */}
          <div className="footer-section footer-about">
            <h3 className="footer-brand">Two Gs Crafting</h3>
            <p className="footer-tagline">Where art meets heart</p>
            <p className="footer-description">
              Specializing in premium custom sublimation, personalized gifts, and
              handcrafted designs. Every piece is crafted with precision and passion
              to bring your unique vision to life.
            </p>
            <div className="footer-trust-badges">
              <div className="trust-badge">
                <Shield size={20} />
                <span>Secure Checkout</span>
              </div>
              <div className="trust-badge">
                <Award size={20} />
                <span>Quality Guaranteed</span>
              </div>
            </div>
          </div>

          {/* Services Section */}
          <div className="footer-section">
            <h4>Our Services</h4>
            <ul>
              <li>Custom Sublimation</li>
              <li>Personalized Gifts</li>
              <li><Link to="/our-work">Our Work</Link></li>
              <li><Link to="/corporate">Corporate & Bulk</Link></li>
              <li>Corporate Gifts</li>
            </ul>
          </div>

          {/* Contact Section */}
          <div className="footer-section">
            <h4>Get in Touch</h4>
            <ul className="footer-contact-info">
              <li>
                <Phone size={16} className="icon" />
                <a href="https://wa.me/+27650717182">+27 65 071 7182</a>
              </li>
              <li>
                <Mail size={16} className="icon" />
                <a href="mailto:2gs.crafting@gmail.com">2gs.crafting@gmail.com</a>
              </li>
              <li>
                <MapPin size={16} className="icon" />
                <span>Mbombela, South Africa</span>
              </li>
              <li>
                <Clock size={16} className="icon" />
                <span>Mon-Fri: 8AM - 5PM</span>
              </li>
            </ul>
          </div>

          {/* Business Info Section */}
          <div className="footer-section">
            <h4>Customer Care</h4>
            <ul>
              <li><Link to="/contact">Contact Us</Link></li>
              <li><Link to="/shipping">Shipping Info</Link></li>
              <li><Link to="/returns">Returns Policy</Link></li>
              <li><Link to="/custom-orders">Custom Orders</Link></li>
            </ul>
            <div className="social-links">
              <a
                href="https://facebook.com/profile.php?id=61582368364446"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                onClick={() => trackSocialClick('facebook')}
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://instagram.com/2gs.crafting/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                onClick={() => trackSocialClick('instagram')}
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.tiktok.com/@2gs.crafting"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                onClick={() => trackSocialClick('tiktok')}
              >
                <TikTokIcon size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <div className="footer-bottom-content">
          <p className="copyright">&copy; {currentYear} Two Gs Crafting. All rights reserved.</p>
          <a className="digital-credit" href="https://twogsdigital.co.za/">Website by Two Gs Digital ↗</a><div className="footer-links-bottom">
            <Link to="/privacy">Privacy Policy</Link>
            <span className="separator">•</span>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
      </div>

      {/* Secret Admin Access */}
      <div
        className="footer-secret-area"
        onClick={handleSecretClick}
        style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          width: '40px',
          height: '40px',
          cursor: 'default',
          zIndex: 10
        }}
      />
    </footer>
  );
};

export default Footer;